import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Card,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Pagination,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllByType, deleteLocal } from '../../services/unifiedFormService';

const TYPE = 'post_work_area_turnover';

export default function PostWorkAreaTurnoverList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // Search removed as requested
    const [filter, setFilter] = useState('all'); // all|draft|submitted
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const rows = await getAllByType(TYPE);
            setItems(rows);
        } catch (e) {
            // ignore
        } finally {
            setLoading(false);
        }
    }

    const filtered = useMemo(() => {
        let rows = items;
        if (filter !== 'all') rows = rows.filter(r => (r.status || r.payload?.status) === filter);
        return rows;
    }, [items, filter]);

    const statusCounts = useMemo(() => ({
        all: items.length,
        draft: items.filter(r => (r.status || r.payload?.status) === 'draft').length,
        submitted: items.filter(r => (r.status || r.payload?.status) === 'submitted').length,
    }), [items]);

    // Pagination calculations
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    }, [filtered, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const cardTitle = (rec) => {
        const p = rec.payload || {};
        const job = p.job || '';
        const jobName = p.jobName || '';
        if (job || jobName) return `${job}${job && jobName ? ' - ' : ''}${jobName}`;
        return rec.uuid;
    };

    return (
        <Box sx={{ 
            height: 'calc(100vh - 60px)', // Account for fixed header
            backgroundColor: '#f5f5f5', 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden' 
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Post-Work Area Turnover</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/forms/post-work-area-turnover')} sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                        New Form
                    </Button>
                </Box>
                <ToggleButtonGroup value={filter} exclusive onChange={(e, v) => v && setFilter(v)} fullWidth size="small">
                    <ToggleButton value="all">All ({statusCounts.all})</ToggleButton>
                    <ToggleButton value="draft">Draft ({statusCounts.draft})</ToggleButton>
                    <ToggleButton value="submitted">Submitted ({statusCounts.submitted})</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No forms found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Click "New Form" to create one</Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => {
                            const p = rec.payload || {};
                            return (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>{cardTitle(rec)}</Typography>
                                                <Chip label={(rec.status || p.status) === 'submitted' ? 'Submitted' : 'Draft'} size="small" sx={{ backgroundColor: (rec.status || p.status) === 'submitted' ? '#4caf50' : '#9e9e9e', color: 'white', height: 20 }} />
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                {p.dhgSupervisor && (
                                                    <Typography variant="caption" color="text.secondary"><strong>Supervisor:</strong> {p.dhgSupervisor}</Typography>
                                                )}
                                                {p.clientRepresentative && (
                                                    <Typography variant="caption" color="text.secondary"><strong>Client Rep:</strong> {p.clientRepresentative}</Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" onClick={() => navigate(`/forms/post-work-area-turnover?uuid=${encodeURIComponent(rec.uuid)}`)} color="primary"><EditIcon /></IconButton>
                                            <IconButton size="small" onClick={async () => { await deleteLocal(rec.uuid); await load(); }} color="error"><DeleteIcon /></IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Stack>
                    
                    {/* Pagination Controls */}
                    {filtered.length > itemsPerPage && (
                        <Box sx={{ 
                            mt: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                                </Typography>
                                <FormControl size="small">
                                    <Select
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                        sx={{ minWidth: 80 }}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Pagination 
                                count={totalPages} 
                                page={currentPage} 
                                onChange={handlePageChange}
                                color="primary"
                                size="medium"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                    </>
                )}
            </Box>
        </Box>
    );
}

